'use client';

import { useIsCpdUsUser } from '@/hooks/useIsCpdSurfaceEnabled';
import { useCPDTranslations } from '@heidi/evidence';
import { Button } from '@heidi/design-system/button';
import {
  TypographyCaption,
  TypographyFootNote,
  TypographyP2,
} from '@heidi/design-system/typography';
import { cn } from '@/lib/cn';
import { type useRouter } from 'next/router';

interface CPDDiscoverabilityCardProps {
  router: ReturnType<typeof useRouter>;
  sourcesCount: number;
  /**
   * Merged onto the outer wrapper, so callers can tune the card's outer
   * spacing — e.g. inset design wraps this in a `p-2` surface that already
   * supplies the bottom inset, so it passes `mb-0` to drop the default `mb-2`.
   */
  className?: string;
}

export const CPDDiscoverabilityCard = ({
  router,
  sourcesCount,
  className,
}: CPDDiscoverabilityCardProps) => {
  const { t } = useCPDTranslations(useIsCpdUsUser());

  return (
    <div className={cn('group/cpd relative mx-1 mb-2 mt-auto', className)}>
      <div
        className={cn(
          'relative overflow-hidden border border-border text-center',
          // Collapsed state styling
          'rounded-lg bg-sand-75 newBrandDark:bg-background-tertiary px-4 py-2',
          // Expanded state styling on hover
          'group-hover/cpd:rounded-xl group-hover/cpd:bg-surface group-hover/cpd:p-4',
          // Smooth transitions for background, border-radius, and padding
          'transition-all duration-200 ease-out'
        )}
      >
        {/* Title - always visible */}
        <TypographyP2 className="font-medium text-text-primary">
          {t('sidenav.cpdPage.discoverabilityButton.title')}
        </TypographyP2>

        {/* Subtitle - always visible */}
        <TypographyFootNote className="mt-1 text-text-tertiary">
          {t('sidenav.cpdPage.sourcesReferenced', { count: sourcesCount })}
        </TypographyFootNote>

        {/* Expanded content - visible on hover with smooth transition */}
        <div
          className={cn(
            'grid overflow-hidden',
            // Collapsed: hidden
            'grid-rows-[0fr] opacity-0',
            // Expanded: visible
            'group-hover/cpd:grid-rows-[1fr] group-hover/cpd:opacity-100',
            // Smooth transition
            'transition-all duration-300 ease-out'
          )}
        >
          <div className="overflow-hidden">
            <div className="mt-3 flex flex-col gap-3">
              <TypographyCaption className="text-text-tertiary">
                {t('sidenav.cpdPage.discoverabilityButton.description')}
              </TypographyCaption>
              <Button
                variant="default"
                size="xs"
                className="w-full"
                onClick={() => {
                  void router.push('/evidence/cpd');
                }}
              >
                {t('sidenav.cpdPage.discoverabilityButton.button')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
